import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ExifReader from "exifreader";
import useBackdropClose from "./useBackdropClose";
import imageCompression from "browser-image-compression";
import {
  FaTimes, FaSchool, FaIdCard, FaMapMarkerAlt, FaUsers, FaUserGraduate, FaChalkboardTeacher,
  FaCamera, FaFilePdf, FaCloudUploadAlt, FaChevronDown, FaExclamationTriangle, FaMinus, FaPlus, FaArrowLeft, FaSpinner,
} from "react-icons/fa";
import axiosConn from "config/axiosConn";
import * as gps from "./gpsFallbackUtils";
import { encryptPdfForTransport } from "../../utils/fileCrypto";
import { useGetEwasteReportQuery, useSubmitEwasteReportMutation } from "../../redux/auth/authApi";
import EWasteBinAnimation from "./EWasteBinAnimation";
import { EWASTE_CATEGORIES, CO_CURRICULAR, AGE_GROUPS, EWASTE_LIMITS as L, isEwasteName, notEwasteMsg, EWASTE_ONLY_MSG } from "./ewasteCatalog";
import "./EWasteModal.css";

/*
 * E-Waste Collection Drive — school report form.
 *
 *   1 Activity        date, workshops, co-curricular activity, description
 *   2 Participants    by age group
 *   3 E-waste         collection corner, collected?, item-wise quantities in
 *                     the five DARPG categories (ITEW, EETW, TLSEW, LIW, OTH)
 *   4 Evidence        1–5 geotagged photos, optional receipt / certificate
 *
 * `exists` (from /swachhata/status) decides whether the saved report is fetched
 * — a school that has never reported makes no read at all. Photos go through
 * /upload-collaboration-file; the report is one POST. Every rule here is
 * enforced again on the server.
 */

const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const itemKey = (code, key) => `${code}:${key}`;
const fieldId = (field) => `ew-${String(field).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
const digits = (v, max) => {
  const s = String(v ?? "").replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 7);
  return s === "" ? "" : String(Math.min(Number(s), max));
};
const num = (v) => Number(v) || 0;

const blankForm = () => ({
  activityDate: todayIST(),
  workshops: "0",
  coCurricular: "",
  description: "",
  participants: { age3to8: "0", age8to11: "0", age11to14: "0", age14to18: "0", age18plus: "0" },
  collectionCorner: "",
  collected: "",
});

function extractGPS(tag, refTag) {
  if (!tag) return NaN;
  let dec = typeof tag.description === "number" ? tag.description : parseFloat(tag.description);
  if (!Number.isFinite(dec) && Array.isArray(tag.value)) {
    const toNum = (x) => (Array.isArray(x) ? (x[1] ? x[0] / x[1] : x[0]) : Number(x));
    const d = toNum(tag.value[0]);
    const m = tag.value.length > 1 ? toNum(tag.value[1]) : 0;
    const s = tag.value.length > 2 ? toNum(tag.value[2]) : 0;
    dec = d + m / 60 + s / 3600;
  }
  if (!Number.isFinite(dec)) return NaN;
  const ref = String(refTag?.description ?? refTag?.value ?? "").trim().toUpperCase();
  if (ref.startsWith("S") || ref.startsWith("W")) dec = -Math.abs(dec);
  return dec;
}

const uploadFiles = async (formData) => {
  const res = await axiosConn("post", "upload-collaboration-file", formData, { headers: { "Content-Type": "multipart/form-data" } });
  const urls = res?.data?.status === "success" ? res.data.data || [] : [];
  if (!urls.length) throw new Error(res?.data?.message || "Upload failed. Please try again.");
  return urls;
};

const safeName = (file, tag) => {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const base = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || tag;
  return `${base}_${new Date().toISOString().replace(/[:.]/g, "-")}_ewaste26.${ext}`;
};

// Client copy of the server rules — returns the first problem, or null.
export const validateEwaste = (form, qty, names, othRows, photos) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.activityDate)) return { field: "activityDate", msg: "Select the date of the activity." };
  if (form.activityDate < "2026-01-01") return { field: "activityDate", msg: "The activity date must be in 2026." };
  if (form.activityDate > todayIST()) return { field: "activityDate", msg: "The activity date cannot be in the future." };
  if (form.workshops === "" || num(form.workshops) > L.workshopsMax) return { field: "workshops", msg: `Enter the number of workshops (0–${L.workshopsMax}).` };
  if (AGE_GROUPS.reduce((t, g) => t + num(form.participants[g.key]), 0) < 1) return { field: "participants", msg: "Enter the participants in at least one age group." };
  if (!form.collectionCorner) return { field: "collectionCorner", msg: "Tell us whether an E-Waste collection corner / bin was set up." };
  if (!form.collected) return { field: "collected", msg: "Tell us whether E-Waste was collected." };
  if (form.collected === "yes") {
    let any = false;
    for (const c of EWASTE_CATEGORIES) {
      if (c.freeform) continue;
      const oq = num(qty[itemKey(c.code, "other")]);
      const on = (names[c.code] || "").trim();
      if (oq > 0 && on.length < 2) return { field: `item:${itemKey(c.code, "other")}`, msg: "Name the other item you collected.", category: c.code };
      // Only electrical / electronic items belong in a named row.
      if (oq > 0 && !isEwasteName(on)) return { field: `item:${itemKey(c.code, "other")}`, msg: notEwasteMsg(on), category: c.code };
      if (c.items.some((it) => num(qty[itemKey(c.code, it.key)]) > 0)) any = true;
    }
    // Others: free rows (name + quantity); blank rows are ignored.
    const seen = new Set();
    for (let i = 0; i < othRows.length; i++) {
      const n = (othRows[i].name || "").replace(/\s+/g, " ").trim();
      const q = num(othRows[i].qty);
      if (!n && q < 1) continue;
      const field = `item:OTH:${i}`;
      if (n.length < 2) return { field, msg: "Type the name of the item.", category: "OTH" };
      if (!isEwasteName(n)) return { field, msg: notEwasteMsg(n), category: "OTH" };
      if (q < 1) return { field, msg: "Enter the quantity for this item.", category: "OTH" };
      if (seen.has(n.toLowerCase())) return { field, msg: "This item is already listed — enter its total quantity once.", category: "OTH" };
      seen.add(n.toLowerCase());
      any = true;
    }
    if (!any) return { field: "items", msg: "Add at least one E-Waste item with its quantity, or answer No to “E-Waste collected”." };
  }
  if (photos.length < L.photosMin) return { field: "photos", msg: "Upload at least one photograph of the activity." };
  return null;
};

export default function EWasteModal({ isOpen, exists = false, onClose, onBack, onSaved }) {
  const user = useSelector((s) => s?.auth?.user) || {};
  const schoolName = user.school_name || "School";
  const udise = user.udise_id || "—";
  const place = [user.state, user.district].filter(Boolean).join(" / ") || "—";

  const { data: saved, isError: savedError, refetch } = useGetEwasteReportQuery(undefined, { skip: !isOpen || !exists });
  const [submitReport, { isLoading: submitting }] = useSubmitEwasteReportMutation();

  const [ready, setReady] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [qty, setQty] = useState({});
  const [names, setNames] = useState({});
  const [othRows, setOthRows] = useState([]);
  const [openCat, setOpenCat] = useState(EWASTE_CATEGORIES[0].code);
  const [photos, setPhotos] = useState([]);
  const [doc, setDoc] = useState(null);
  const [busyPhotos, setBusyPhotos] = useState(0);
  const [busyDoc, setBusyDoc] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const bodyRef = useRef(null);

  // Fill the form once per opening: blank for a new report, the saved report
  // (fetched only for schools that have one) for an update.
  useEffect(() => {
    if (!isOpen) {
      setReady(false);
      return;
    }
    if (ready) return;
    if (exists && !saved) return;
    const r = exists ? saved?.report : null;
    if (r) {
      setForm({
        activityDate: r.activityDate || todayIST(),
        workshops: String(r.workshops ?? 0),
        coCurricular: r.coCurricular || "",
        description: r.description || "",
        participants: Object.fromEntries(AGE_GROUPS.map((g) => [g.key, String(r.participants?.[g.key] ?? 0)])),
        collectionCorner: r.collectionCorner || "",
        collected: r.collected || "",
      });
      const q = {};
      const n = {};
      const rows = [];
      (r.items || []).forEach((i) => {
        if (i.category === "OTH") {
          rows.push({ name: i.name || "", qty: String(i.qty) });
          return;
        }
        q[itemKey(i.category, i.item)] = String(i.qty);
        if (i.item === "other") n[i.category] = i.name || "";
      });
      setQty(q);
      setNames(n);
      setOthRows(rows);
      setOpenCat((r.items || [])[0]?.category || EWASTE_CATEGORIES[0].code);
      setPhotos((r.photos || []).map((p) => ({ ...p, preview: p.url })));
      setDoc(r.document || null);
    } else {
      setForm(blankForm());
      setQty({});
      setNames({});
      setOthRows([]);
      setOpenCat(EWASTE_CATEGORIES[0].code);
      setPhotos([]);
      setDoc(null);
    }
    setErrors({});
    setBanner("");
    setReady(true);
  }, [isOpen, exists, saved, ready]);

  const catTotals = useMemo(() => {
    const t = {};
    let all = 0;
    EWASTE_CATEGORIES.forEach((c) => {
      t[c.code] = c.freeform ? othRows.reduce((s, r) => s + num(r.qty), 0) : c.items.reduce((s, it) => s + num(qty[itemKey(c.code, it.key)]), 0);
      all += t[c.code];
    });
    return { ...t, all };
  }, [qty, othRows]);
  const participantsTotal = AGE_GROUPS.reduce((t, g) => t + num(form.participants[g.key]), 0);

  // Clicking the dimmed area around the form closes it like "Cancel" — never
  // while the report is being saved.
  const backdrop = useBackdropClose(() => {
    if (!submitting) onClose();
  });

  if (!isOpen) return null;

  const clearErr = (field) => {
    if (errors[field] || errors.items || banner) {
      setErrors((e) => {
        const n = { ...e };
        delete n[field];
        if (field.startsWith("item:")) delete n.items;
        return n;
      });
      setBanner("");
    }
  };
  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    clearErr(k);
  };
  const setAge = (k, v) => {
    setForm((f) => ({ ...f, participants: { ...f.participants, [k]: digits(v, L.countMax) } }));
    clearErr("participants");
  };
  const setItemQty = (code, key, v) => {
    setQty((q) => ({ ...q, [itemKey(code, key)]: digits(v, L.quantityMax) }));
    clearErr(`item:${itemKey(code, key)}`);
  };
  const step = (code, key, delta) => setItemQty(code, key, Math.max(0, num(qty[itemKey(code, key)]) + delta));
  const setOth = (i, patch) => {
    setOthRows((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    clearErr(`item:OTH:${i}`);
  };
  const removeOth = (i) => setOthRows((rows) => rows.filter((_, j) => j !== i));
  // Tell the school as soon as they leave the box, not only on submit.
  const flagNotEwaste = (field, value) => {
    const n = String(value || "").replace(/\s+/g, " ").trim();
    if (n.length >= 2 && !isEwasteName(n)) setErrors((e) => ({ ...e, [field]: notEwasteMsg(n) }));
  };

  const showProblem = (p) => {
    setErrors({ [p.field]: p.msg });
    setBanner(p.msg);
    if (p.category) setOpenCat(p.category);
    else if (p.field.startsWith("item:")) setOpenCat(p.field.slice(5).split(":")[0]);
    setTimeout(() => document.getElementById(fieldId(p.field))?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  // ── uploads ──────────────────────────────────────────────────────────────
  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || busyPhotos) return;
    const room = L.photosMax - photos.length;
    if (room <= 0) return showProblem({ field: "photos", msg: `You can upload at most ${L.photosMax} photos. Remove one to add another.` });
    const picked = files.slice(0, room);
    setBusyPhotos(picked.length);
    clearErr("photos");
    try {
      let coords;
      const prepared = [];
      for (const file of picked) {
        if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) throw new Error(`"${file.name}" is not a JPG or PNG photo.`);
        let lat = NaN;
        let lng = NaN;
        try {
          const tags = await ExifReader.load(await file.arrayBuffer());
          lat = extractGPS(tags?.GPSLatitude, tags?.GPSLatitudeRef);
          lng = extractGPS(tags?.GPSLongitude, tags?.GPSLongitudeRef);
        } catch (e) {
          /* no EXIF */
        }
        let out = file;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          if (coords === undefined) {
            try {
              coords = await gps.getCurrentCoords();
            } catch (e) {
              coords = null;
            }
          }
          if (coords && Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)) {
            lat = coords.latitude;
            lng = coords.longitude;
            if (typeof gps.embedGpsExif === "function") {
              try {
                out = await gps.embedGpsExif(file, lat, lng);
              } catch (e) {
                /* keep the original */
              }
            }
          }
        }
        if (out.size > L.fileMaxMb * 1024 * 1024) {
          const blob = await imageCompression(out, { maxSizeMB: L.fileMaxMb - 1, maxWidthOrHeight: 2560, useWebWorker: true, preserveExif: true });
          out = new File([blob], out.name, { type: out.type || file.type });
        }
        prepared.push({
          // type + extension follow the actual bytes (embedGpsExif turns PNG into JPEG)
          file: new File([out], safeName(out, "photo"), { type: out.type || file.type }),
          lat: Number.isFinite(lat) ? lat : null,
          lng: Number.isFinite(lng) ? lng : null,
          preview: URL.createObjectURL(out),
        });
      }
      const added = [];
      for (let i = 0; i < prepared.length; i += 3) {
        const chunk = prepared.slice(i, i + 3);
        const fd = new FormData();
        chunk.forEach((p) => fd.append("files", p.file));
        const urls = await uploadFiles(fd);
        urls.forEach((url, j) => chunk[j] && added.push({ url, lat: chunk[j].lat, lng: chunk[j].lng, preview: chunk[j].preview }));
      }
      setPhotos((prev) => [...prev, ...added].slice(0, L.photosMax));
    } catch (err) {
      showProblem({ field: "photos", msg: err?.response?.data?.message || err?.message || "Upload failed. Please try again." });
    } finally {
      setBusyPhotos(0);
    }
  };

  const addDocument = async (file) => {
    if (!file || busyDoc) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const isPdf = file.type === "application/pdf" && ext === "pdf";
    const isImage = ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
    setBusyDoc(true);
    clearErr("document");
    try {
      if (!isPdf && !isImage) throw new Error("Upload the receipt / certificate as a PDF, JPG or PNG file.");
      if (file.size > L.fileMaxMb * 1024 * 1024) throw new Error(`The file must be smaller than ${L.fileMaxMb} MB.`);
      const renamed = new File([file], safeName(file, "receipt"), { type: file.type });
      const fd = new FormData();
      if (isPdf) {
        const { encryptedFile, metadata } = await encryptPdfForTransport(renamed);
        Object.entries(metadata).forEach(([k, v]) => fd.append(k, v));
        fd.append("files", encryptedFile);
      } else {
        fd.append("files", renamed);
      }
      const [url] = await uploadFiles(fd);
      setDoc({ url, name: file.name, type: isPdf ? "pdf" : "image" });
    } catch (err) {
      showProblem({ field: "document", msg: err?.response?.data?.message || err?.message || "Upload failed. Please try again." });
    } finally {
      setBusyDoc(false);
    }
  };

  // ── submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || busyPhotos || busyDoc) return;
    const problem = validateEwaste(form, qty, names, othRows, photos);
    if (problem) return showProblem(problem);
    const items =
      form.collected === "yes"
        ? EWASTE_CATEGORIES.flatMap((c) =>
            c.freeform
              ? othRows
                  .map((r) => ({ name: (r.name || "").replace(/\s+/g, " ").trim(), qty: num(r.qty) }))
                  .filter((r) => r.name && r.qty > 0)
                  .map((r) => ({ category: c.code, item: "other", qty: r.qty, name: r.name }))
              : c.items
              .filter((it) => num(qty[itemKey(c.code, it.key)]) > 0)
              .map((it) => ({ category: c.code, item: it.key, qty: num(qty[itemKey(c.code, it.key)]), name: it.key === "other" ? (names[c.code] || "").trim() : "" }))
          )
        : [];
    try {
      const res = await submitReport({
        activityDate: form.activityDate,
        workshops: num(form.workshops),
        coCurricular: form.coCurricular,
        description: form.description.replace(/\s+/g, " ").trim(),
        participants: Object.fromEntries(AGE_GROUPS.map((g) => [g.key, num(form.participants[g.key])])),
        collectionCorner: form.collectionCorner,
        collected: form.collected,
        items,
        photos: photos.map((p) => ({ url: p.url, lat: p.lat, lng: p.lng })),
        document: doc ? { url: doc.url, name: doc.name } : null,
      }).unwrap();
      onSaved && onSaved(res);
    } catch (err) {
      const d = err?.data || {};
      if (d.field) showProblem({ field: d.field, msg: d.message });
      else setBanner(d.message || "Could not save the report. Please check your connection and try again.");
    }
  };

  const Seg = ({ field, options }) => (
    <div className="ewx-seg" id={fieldId(field)} role="radiogroup">
      {options.map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={form[field] === value}
          className={`ewx-seg__btn ${form[field] === value ? "is-on" : ""} ewx-seg__btn--${value}`}
          onClick={() => setField(field, value)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const loadingSaved = exists && !ready;

  return (
    <div className="ewaste-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="ewaste-modal-title" {...backdrop}>
      <div className="ewaste-modal-container ewx-wide">
        <div className="ewaste-modal-header">
          <div className="ewaste-header-brand-wrap">
            {onBack && (
              <button type="button" className="ewaste-header-back-btn" onClick={onBack} aria-label="Back">
                <FaArrowLeft />
              </button>
            )}
            <EWasteBinAnimation />
            <div className="ewaste-header-text-group">
              <div id="ewaste-modal-title" className="ewaste-header-title">
                {exists ? "Update E-Waste Collection Drive" : "E-Waste Collection Drive"}
              </div>
              <p className="ewaste-header-subtitle">
                Report the activity, the participants and the E-Waste your school collected — item-wise, in numbers.
              </p>
            </div>
          </div>
          <button type="button" className="ewaste-close-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {loadingSaved ? (
          <div className="ewaste-modal-body">
            {savedError ? (
              <div className="ewx-alert">
                <FaExclamationTriangle /> <span>Could not load your saved report.</span>
                <button type="button" className="ewx-link" onClick={refetch}>Try again</button>
              </div>
            ) : (
              <div className="ewx-skeleton" aria-busy="true">
                <span /><span /><span /><span />
              </div>
            )}
          </div>
        ) : (
          <form id="ewx-form" onSubmit={handleSubmit} className="ewaste-modal-body" ref={bodyRef} noValidate>
            <div className="ewaste-school-card mb-3">
              <div className="ewaste-school-row">
                <div className="ewaste-school-item">
                  <span className="ewaste-school-icon"><FaSchool /></span>
                  <div>
                    <div className="label">School</div>
                    <div className="value" title={schoolName}>{schoolName}</div>
                  </div>
                </div>
                <div className="ewaste-school-item">
                  <span className="ewaste-school-icon"><FaIdCard /></span>
                  <div>
                    <div className="label">UDISE Code</div>
                    <div className="value">{udise}</div>
                  </div>
                </div>
                <div className="ewaste-school-item">
                  <span className="ewaste-school-icon"><FaMapMarkerAlt /></span>
                  <div>
                    <div className="label">State / District</div>
                    <div className="value" title={place}>{place}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 1 — Activity */}
            <section className="ewx-section">
              <div className="ewx-section__head">
                <span className="ewx-step">1</span>
                <div>
                  <div className="ewx-section__title">Activity details</div>
                  <div className="ewx-section__sub">When the drive was held and what was done</div>
                </div>
              </div>
              <div className="ewx-grid2">
                <div className="ewaste-form-group" id={fieldId("activityDate")}>
                  <label className="ewaste-form-label" htmlFor="ewx-date">Date of activity <span className="text-danger">*</span></label>
                  <input id="ewx-date" type="date" className="ewaste-input" value={form.activityDate} min="2026-01-01" max={todayIST()} onChange={(e) => setField("activityDate", e.target.value)} />
                  {errors.activityDate && <div className="ewaste-error">{errors.activityDate}</div>}
                </div>
                <div className="ewaste-form-group" id={fieldId("workshops")}>
                  <label className="ewaste-form-label" htmlFor="ewx-workshops">Workshops conducted <span className="text-danger">*</span></label>
                  <input id="ewx-workshops" type="text" inputMode="numeric" className="ewaste-input" value={form.workshops} onChange={(e) => setField("workshops", digits(e.target.value, L.workshopsMax))} />
                  {errors.workshops && <div className="ewaste-error">{errors.workshops}</div>}
                </div>
              </div>
              <div className="ewaste-form-group">
                <label className="ewaste-form-label" htmlFor="ewx-cc">Co-curricular activity</label>
                <select id="ewx-cc" className="ewaste-select" value={form.coCurricular} onChange={(e) => setField("coCurricular", e.target.value)}>
                  <option value="">Select (optional)</option>
                  {CO_CURRICULAR.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              <div className="ewaste-form-group mb-0" id={fieldId("description")}>
                <label className="ewaste-form-label" htmlFor="ewx-desc">
                  Brief description of the activity <span className="ewx-optional">(optional)</span>
                  <span className="ewx-counter">{`${form.description.length}/${L.descriptionMax}`}</span>
                </label>
                <textarea id="ewx-desc" rows="3" maxLength={L.descriptionMax} className="ewaste-textarea" placeholder="What did the school do in the drive?" value={form.description} onChange={(e) => setField("description", e.target.value)} />
                {errors.description && <div className="ewaste-error">{errors.description}</div>}
              </div>
            </section>

            {/* 2 — Participants */}
            <section className="ewx-section" id={fieldId("participants")}>
              <div className="ewx-section__head">
                <span className="ewx-step">2</span>
                <div>
                  <div className="ewx-section__title">Participants</div>
                  <div className="ewx-section__sub">Number of people by age group</div>
                </div>
                <span className="ewx-pill"><FaUsers /> {`Total ${participantsTotal.toLocaleString("en-IN")}`}</span>
              </div>
              <div className="ewaste-participants-grid">
                {AGE_GROUPS.map((g) => (
                  <label key={g.key} className="ewaste-age-box">
                    <span className="ewaste-age-icon-ring">{g.teacher ? <FaChalkboardTeacher /> : <FaUserGraduate />}</span>
                    <span className="ewaste-age-label">{g.label}</span>
                    <input type="text" inputMode="numeric" className="ewaste-age-input" value={form.participants[g.key]} onChange={(e) => setAge(g.key, e.target.value)} onFocus={(e) => e.target.select()} />
                  </label>
                ))}
              </div>
              {errors.participants && <div className="ewaste-error text-center">{errors.participants}</div>}
            </section>

            {/* 3 — E-waste collected */}
            <section className="ewx-section">
              <div className="ewx-section__head">
                <span className="ewx-step">3</span>
                <div>
                  <div className="ewx-section__title">E-Waste collected</div>
                  <div className="ewx-section__sub">Quantity in numbers (Nos.) · categories of the E-Waste (Management) Rules, 2022</div>
                </div>
                {form.collected === "yes" && catTotals.all > 0 && <span className="ewx-pill ewx-pill--dark">{`${catTotals.all.toLocaleString("en-IN")} items`}</span>}
              </div>

              <div className="ewx-grid2">
                <div className="ewaste-form-group">
                  <div className="ewaste-form-label">E-Waste collection corner / bin set up? <span className="text-danger">*</span></div>
                  <Seg field="collectionCorner" options={[["yes", "Yes"], ["no", "No"], ["na", "Not applicable"]]} />
                  {errors.collectionCorner && <div className="ewaste-error">{errors.collectionCorner}</div>}
                </div>
                <div className="ewaste-form-group">
                  <div className="ewaste-form-label">Was E-Waste collected? <span className="text-danger">*</span></div>
                  <Seg field="collected" options={[["yes", "Yes"], ["no", "No"]]} />
                  {errors.collected && <div className="ewaste-error">{errors.collected}</div>}
                </div>
              </div>

              {form.collected === "yes" && (
                <div className="ewx-cats" id={fieldId("items")}>
                  <div className="ewx-cats__hint">Open a category and tap <b>Add</b> next to each item your school collected.</div>
                  <div className="ewx-only" role="note">
                    <FaExclamationTriangle aria-hidden="true" />
                    <span><b>{EWASTE_ONLY_MSG}</b> Anything that is not electrical or electronic — paper, plastic, food, fruit, garden waste — will be rejected.</span>
                  </div>
                  {errors.items && <div className="ewaste-error mb-2">{errors.items}</div>}

                  {EWASTE_CATEGORIES.map((c) => {
                    const open = openCat === c.code;
                    const total = catTotals[c.code];
                    return (
                      <div key={c.code} className={`ewx-cat ${open ? "is-open" : ""}`} style={{ "--tone": c.tone }}>
                        <button type="button" className="ewx-cat__head" aria-expanded={open} onClick={() => setOpenCat(open ? "" : c.code)}>
                          <span className="ewx-cat__dot" aria-hidden="true" />
                          <span className="ewx-cat__title">{c.title}</span>
                          {total > 0 && <span className="ewx-cat__count is-set">{`${total.toLocaleString("en-IN")} Nos.`}</span>}
                          <FaChevronDown className="ewx-cat__chev" />
                        </button>

                        {open && c.freeform && (
                          <div className="ewx-list ewx-list--free">
                            <div className="ewx-free-hint">Type the name of each item and how many pieces were collected.</div>
                            <div className="ewx-only ewx-only--tight" role="note">
                              <FaExclamationTriangle aria-hidden="true" />
                              <span><b>Only E-Waste can be entered here.</b> Enter electrical or electronic items only (battery, charger, bulb, fan, mobile phone, computer part). Paper, plastic, food, fruit and garden waste are not E-Waste and will be rejected.</span>
                            </div>
                            {othRows.map((r, i) => {
                              const f = `item:OTH:${i}`;
                              const err = errors[f];
                              return (
                                <div key={i} id={fieldId(f)} className={`ewx-row ewx-row--free ${err ? "is-error" : ""}`}>
                                  <input
                                    type="text"
                                    className="ewaste-input ewx-row__name"
                                    maxLength={L.otherNameMax}
                                    placeholder="Name of the item (E-Waste only)"
                                    value={r.name}
                                    onChange={(e) => setOth(i, { name: e.target.value })}
                                    onBlur={(e) => flagNotEwaste(f, e.target.value)}
                                  />
                                  <div className="ewx-stepper">
                                    <button type="button" onClick={() => setOth(i, { qty: digits(Math.max(0, num(r.qty) - 1), L.quantityMax) })} disabled={!num(r.qty)} aria-label="Decrease"><FaMinus /></button>
                                    <input type="text" inputMode="numeric" placeholder="0" value={r.qty} aria-label={`Item ${i + 1} quantity`} onChange={(e) => setOth(i, { qty: digits(e.target.value, L.quantityMax) })} />
                                    <button type="button" onClick={() => setOth(i, { qty: digits(num(r.qty) + 1, L.quantityMax) })} aria-label="Increase"><FaPlus /></button>
                                  </div>
                                  <button type="button" className="ewx-remove" onClick={() => removeOth(i)} aria-label={`Remove item ${i + 1}`} title="Remove"><FaTimes /></button>
                                  {err && <div className="ewaste-error ewx-row__err">{err}</div>}
                                </div>
                              );
                            })}
                            {othRows.length < L.othRowsMax && (
                              <button type="button" className="ewx-add" onClick={() => setOthRows((rows) => [...rows, { name: "", qty: "1" }])}>
                                <FaPlus /> {othRows.length ? "Add another item" : "Add an item"}
                              </button>
                            )}
                          </div>
                        )}

                        {open && !c.freeform && (
                          <div className="ewx-list">
                            {c.items.map((it) => {
                              const k = itemKey(c.code, it.key);
                              const v = qty[k] || "";
                              const on = num(v) > 0;
                              const err = errors[`item:${k}`];
                              const isOther = it.key === "other";
                              return (
                                <div key={k} id={fieldId(`item:${k}`)} className={`ewx-row ${on ? "is-set" : ""} ${isOther && on ? "ewx-row--other" : ""} ${err ? "is-error" : ""}`}>
                                  <span className="ewx-row__label">{it.label}</span>
                                  {isOther && on && (
                                    <input
                                      type="text"
                                      className="ewaste-input ewx-row__name"
                                      maxLength={L.otherNameMax}
                                      placeholder="Name of the item (E-Waste only)"
                                      value={names[c.code] || ""}
                                      onChange={(e) => {
                                        setNames((n) => ({ ...n, [c.code]: e.target.value }));
                                        clearErr(`item:${k}`);
                                      }}
                                      onBlur={(e) => flagNotEwaste(`item:${k}`, e.target.value)}
                                    />
                                  )}
                                  {on ? (
                                    <div className="ewx-stepper">
                                      <button type="button" onClick={() => step(c.code, it.key, -1)} aria-label={`Decrease ${it.label}`}><FaMinus /></button>
                                      <input type="text" inputMode="numeric" value={v} aria-label={`${it.label} quantity`} onChange={(e) => setItemQty(c.code, it.key, e.target.value)} />
                                      <button type="button" onClick={() => step(c.code, it.key, 1)} aria-label={`Increase ${it.label}`}><FaPlus /></button>
                                    </div>
                                  ) : (
                                    <button type="button" className="ewx-addqty" onClick={() => setItemQty(c.code, it.key, 1)} aria-label={`Add ${it.label}`}>
                                      <FaPlus /> Add
                                    </button>
                                  )}
                                  {err && <div className="ewaste-error ewx-row__err">{err}</div>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {form.collected === "no" && <div className="ewx-note">No items to add — you can still report the awareness activity and upload its photos.</div>}
            </section>

            {/* 4 — Evidence */}
            <section className="ewx-section">
              <div className="ewx-section__head">
                <span className="ewx-step">4</span>
                <div>
                  <div className="ewx-section__title">Photos &amp; documents</div>
                  <div className="ewx-section__sub">Geotagged photos of the drive (JPG / PNG) · receipt or recycling certificate if any</div>
                </div>
                <span className="ewaste-count-badge">{`${photos.length} / ${L.photosMax}`}</span>
              </div>
              <div className="ewx-grid2">
                <div id={fieldId("photos")}>
                  <div className="ewaste-form-label">Activity photos <span className="text-danger">*</span></div>
                  <div className="ewaste-preview-grid mb-2">
                    {photos.map((p, i) => (
                      <div key={p.url} className="ewaste-preview-thumb-card">
                        <a href={p.url} target="_blank" rel="noopener noreferrer"><img src={p.preview || p.url} alt={`Photo ${i + 1}`} className="ewaste-preview-thumb-img" /></a>
                        {p.lat != null && <span className="ewx-geo" title={`${p.lat}, ${p.lng}`}><FaMapMarkerAlt /></span>}
                        <button type="button" className="ewaste-thumb-close-btn" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))} aria-label="Remove photo"><FaTimes /></button>
                      </div>
                    ))}
                    {Array.from({ length: busyPhotos }, (_, i) => <div key={`b${i}`} className="ewaste-preview-thumb-card ewx-thumb-busy"><FaSpinner className="ewx-spin" /></div>)}
                  </div>
                  {photos.length + busyPhotos < L.photosMax && (
                    <label className={`ewaste-upload-dropzone compact ewx-drop ${busyPhotos ? "is-busy" : ""}`}>
                      <input type="file" accept="image/jpeg,image/png" multiple hidden disabled={!!busyPhotos} onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
                      <span className="ewaste-upload-icon compact"><FaCamera /></span>
                      <span className="ewaste-upload-title">{busyPhotos ? "Uploading photos…" : "Add photos"}</span>
                      <span className="ewaste-upload-subtitle">{`Up to ${L.photosMax} · location is added from the photo or this device`}</span>
                    </label>
                  )}
                  {errors.photos && <div className="ewaste-error">{errors.photos}</div>}
                </div>
                <div id={fieldId("document")}>
                  <div className="ewaste-form-label">Receipt / certificate (optional)</div>
                  {doc ? (
                    <div className="ewaste-doc-thumb-wrapper">
                      <div className="ewaste-preview-thumb-card ewaste-doc-single-card">
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                          {doc.type === "pdf" ? (
                            <span className="ewaste-pdf-preview-box"><FaFilePdf size={30} color="#d32f2f" /><span className="ewaste-pdf-name">PDF</span></span>
                          ) : (
                            <img src={doc.url} alt="Receipt" className="ewaste-preview-thumb-img" />
                          )}
                        </a>
                        <button type="button" className="ewaste-thumb-close-btn" onClick={() => setDoc(null)} aria-label="Remove document"><FaTimes /></button>
                      </div>
                      <div className="ewaste-doc-caption"><span className="text-truncate">{doc.name || "Document"}</span></div>
                    </div>
                  ) : (
                    <label className={`ewaste-upload-dropzone compact ewx-drop ${busyDoc ? "is-busy" : ""}`}>
                      <input type="file" accept="application/pdf,image/jpeg,image/png" hidden disabled={busyDoc} onChange={(e) => { addDocument(e.target.files?.[0]); e.target.value = ""; }} />
                      <span className="ewaste-upload-icon compact">{busyDoc ? <FaSpinner className="ewx-spin" /> : <FaCloudUploadAlt />}</span>
                      <span className="ewaste-upload-title">{busyDoc ? "Uploading…" : "Upload receipt / certificate"}</span>
                      <span className="ewaste-upload-subtitle">{`PDF, JPG or PNG · max ${L.fileMaxMb} MB`}</span>
                    </label>
                  )}
                  {errors.document && <div className="ewaste-error">{errors.document}</div>}
                </div>
              </div>
            </section>
          </form>
        )}

        <div className="ewaste-modal-footer">
          <div className="ewx-footer-info">
            {banner ? (
              <span className="ewx-footer-error"><FaExclamationTriangle /> {banner}</span>
            ) : (
              <span>{`${form.collected === "yes" ? `${catTotals.all.toLocaleString("en-IN")} items · ` : ""}${participantsTotal.toLocaleString("en-IN")} participants · ${photos.length} photo${photos.length === 1 ? "" : "s"}`}</span>
            )}
          </div>
          <div className="ewaste-footer-right">
            <button type="button" className="ewaste-btn ewaste-btn-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" form="ewx-form" className="ewaste-btn ewaste-btn-submit" disabled={loadingSaved || submitting || !!busyPhotos || busyDoc}>
              {submitting ? <><FaSpinner className="ewx-spin" /> Saving…</> : exists ? "Update report" : "Submit report"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
